import { z } from "zod";
import { EventParticipantIdPublicSchema } from "./branded-id.schemas.js";
import { dataWithRolesSchema } from "./data-with-roles.schemas.js";
import { localizedTextSchema } from "./localized-text-json.schemas.js";
import {
  IntroOriginSchema,
  IntroOriginDataSchema,
} from "./intro-origin.schemas.js";
import {
  IntroRequestStatusSchema,
  INTRO_REQUEST_TYPE,
} from "./intro-request-state.schemas.js";
import { ConnectionRequestRoleSchema } from "../constants/connection-request-role.constants.js";
import { statusHistoryEntrySchema } from "./status-history.schemas.js";
import { _connectionRequestSchema } from "./connection-request.schemas.js";

// Party view hides per-transition metadata; only state + timestamp.
const _connectionRequestPartyStatusHistoryEntrySchema =
  statusHistoryEntrySchema(IntroRequestStatusSchema).omit({
    metadata: true,
  });

const _connectionRequestPartyTypeDataSchema = z.object({
  type: z.literal(INTRO_REQUEST_TYPE),
  origin: IntroOriginSchema,
  message: localizedTextSchema.nullable(),
  requesterParticipantIdPublic: EventParticipantIdPublicSchema,
  targetParticipantIdPublic: EventParticipantIdPublicSchema,
  status: IntroRequestStatusSchema,
  statusHistory: z
    .array(_connectionRequestPartyStatusHistoryEntrySchema)
    .readonly(),
});

const _connectionRequestPartyViewSchema = _connectionRequestSchema
  .pick({
    idPublic: true,
    type: true,
  })
  .extend({
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    typeData: _connectionRequestPartyTypeDataSchema,
  });

// $roles stamped via dataWithRolesSchema so frontend can gate UI by membership.
export const ConnectionRequestPartyViewSchema = dataWithRolesSchema(
  _connectionRequestPartyViewSchema.readonly(),
  ConnectionRequestRoleSchema,
);
export type ConnectionRequestPartyView = z.infer<
  typeof ConnectionRequestPartyViewSchema
>;

const _connectionRequestManagerStatusHistoryEntrySchema =
  statusHistoryEntrySchema(IntroRequestStatusSchema);

const _connectionRequestManagerTypeDataSchema =
  _connectionRequestPartyTypeDataSchema.extend({
    originData: IntroOriginDataSchema.nullable(),
    statusHistory: z
      .array(_connectionRequestManagerStatusHistoryEntrySchema)
      .readonly(),
  });

const _connectionRequestManagerViewSchema =
  _connectionRequestPartyViewSchema.extend({
    typeData: _connectionRequestManagerTypeDataSchema,
  });

export const ConnectionRequestManagerViewSchema = dataWithRolesSchema(
  _connectionRequestManagerViewSchema.readonly(),
  ConnectionRequestRoleSchema,
);
export type ConnectionRequestManagerView = z.infer<
  typeof ConnectionRequestManagerViewSchema
>;

const _connectionRequestAdminViewSchema = _connectionRequestSchema.extend({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  typeData: _connectionRequestManagerTypeDataSchema,
});

export const ConnectionRequestAdminViewSchema = dataWithRolesSchema(
  _connectionRequestAdminViewSchema.readonly(),
  ConnectionRequestRoleSchema,
);
export type ConnectionRequestAdminView = z.infer<
  typeof ConnectionRequestAdminViewSchema
>;
export type ConnectionRequestViewUnion =
  | ConnectionRequestAdminView
  | ConnectionRequestManagerView
  | ConnectionRequestPartyView;
